import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
  Checkbox,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Tooltip,
  Fade,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useThemeContext } from '../../Context/ThemeContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSelectedSourceEntities,
  setSelectedDestinationEntities,
  fetchEntitiesIfNeeded,
  setConfirmedSourceEntities,
  setConfirmedDestinationEntities,
} from '../../state/slices/entitiesSlice';
import {
  runFuzzyLogicIfNeeded,
  setAutoFuzzyEnabled,
  setExpandAllTrigger,
  setHasVisitedMapping,
} from '../../state/slices/mappingSlice';
import SourceIcon from '../images/noun-open.png';
import DestinationIcon from '../images/target-icon.png';
import { areSelectionsEqual } from '../../utils/arrayUtils/comparator';
import {
  getFilteredList,
  sortSelectedToTop,
} from '../../utils/arrayUtils/sorts';
import ConfirmDialog from './ConfirmDialog';
import { getErrorMessage } from '../../utils/formatters/errorRespConverter';
import { resolveFieldsService } from '../../apis/EntitiesSelection';
import { toast } from 'react-toastify';
import { fetchSqlTableFields, getConnectionById } from '../../apis/Connections';

const EntitySelectionDialog = ({
  open,
  onClose,
  selectedNode,
  updatedConfig,
  requireEntityChangeForNext,
  onNext,

  restrictToActiveDest = false,
  activeDestinationId,
  lockSource = false,
  decisionMeta,
  secondaryDestinationId,
  hideAutoFuzzy = false,
  onRequestSqlConnectionFix,
}) => {
  const [sourceSearchVisible, setSourceSearchVisible] = useState({});
  const [sourceSearchTerm, setSourceSearchTerm] = useState({});

  const [destinationSearchVisible, setDestinationSearchVisible] = useState({});
  const [destinationSearchTerm, setDestinationSearchTerm] = useState({});

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingUnselectEntity, setPendingUnselectEntity] = useState(null);
  const [pendingUnselectType, setPendingUnselectType] = useState(null);
  const [pendingUnselectKey, setPendingUnselectKey] = useState(null);
  const sqlErrorToastKeysRef = useRef(new Set());

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { workflowName: workflowId } = useParams();

  const connectionDetails = useSelector(
    (state) => state.workflows.workflows?.[workflowId]?.connectionDetails || [],
  );

  const workflow = useSelector(
    (state) => state.workflows.workflows?.[workflowId] || {},
  );

  const entitySelections = workflow?.entitySelections || {};
  const hasEntitySelections =
    (entitySelections.source &&
      Object.keys(entitySelections.source).length > 0) ||
    (entitySelections.destination &&
      Object.keys(entitySelections.destination).length > 0) ||
    false;

  console.log('entity source::', entitySelections.source);
  console.log('entity destination::', entitySelections.destination);
  console.log('hasEntitySelections::', hasEntitySelections);
  console.log('workflow mapping json:', connectionDetails);

  const hasSavedMappingInDB =
    Array.isArray(connectionDetails) &&
    connectionDetails.some(
      (conn) => Array.isArray(conn?.mappings) && conn.mappings.length > 0,
    );

  const autoFuzzyEnabled = useSelector(
    (state) => state.mapping.autoFuzzyEnabledByWorkflow?.[workflowId],
  );
  const hasVisitedMapping = useSelector(
    (state) => state.mapping.hasVisitedMappingByWorkflow?.[workflowId] ?? false,
  );

  const shouldSkipFuzzy = !hasVisitedMapping && hasSavedMappingInDB;

  const effectiveAutoFuzzy = shouldSkipFuzzy
    ? false
    : (autoFuzzyEnabled ?? true);

  console.log('shouldSkipFuzzy:', shouldSkipFuzzy);

  const findSourceNodes = (nodes) =>
    nodes.filter((n) => n.type === 'sourceNode' || n.type === 'source');
  const findDestNodes = (nodes) =>
    nodes.filter(
      (n) => n.type === 'destinationNode' || n.type === 'destination',
    );

  const viewNodes = useMemo(() => {
    const nodes = updatedConfig?.nodes || [];
    if (!restrictToActiveDest || !activeDestinationId) return nodes;
    const sources = nodes.filter(
      (n) => n.type === 'sourceNode' || n.type === 'source',
    );
    const activeDest = nodes.filter(
      (n) =>
        (n.type === 'destinationNode' || n.type === 'destination') &&
        n.id === activeDestinationId,
    );
    return [...sources, ...activeDest];
  }, [updatedConfig, restrictToActiveDest, activeDestinationId]);

  const sourceNodes = findSourceNodes(viewNodes);
  const destNodes = findDestNodes(viewNodes);

  const buildNodeKey = (node) => {
    const nodeModule = node.config?.module || '';
    // Normalize SQL module to lowercase for consistency
    const normalizedModule =
      nodeModule && nodeModule.toLowerCase() === 'sql' ? 'sql' : nodeModule;
    return `${node.id}-${node.config?.connection?.conn_id || ''}-${normalizedModule}`;
  };

  const findNodeByConnection = (nodes = [], { connId, connName }) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;

    if (connId) {
      const byId = nodes.find(
        (node) =>
          String(node?.config?.connection?.conn_id || '') === String(connId),
      );
      if (byId) return byId;
    }

    if (connName) {
      const byName = nodes.find(
        (node) =>
          (node?.config?.connection?.conn_name || '').toLowerCase() ===
          connName.toLowerCase(),
      );
      if (byName) return byName;
    }

    return null;
  };

  const sourceNodeKeyMap = useMemo(() => {
    const map = new Map();
    sourceNodes.forEach((node) => {
      map.set(buildNodeKey(node), node);
    });
    return map;
  }, [sourceNodes]);

  const destinationNodeKeyMap = useMemo(() => {
    const map = new Map();
    destNodes.forEach((node) => {
      map.set(buildNodeKey(node), node);
    });
    return map;
  }, [destNodes]);

  console.log('updatedConfig :', updatedConfig);

  console.log('sourceNode:', updatedConfig?.nodes);

  const sourceLoadingByWorkflow = useSelector(
    (state) => state.entities?.sourceLoadingByWorkflow ?? {},
  );
  const destinationLoadingByWorkflow = useSelector(
    (state) => state.entities?.destinationLoadingByWorkflow ?? {},
  );

  const sourceEntitiesByWorkflow = useSelector(
    (state) => state.entities?.entitiesByWorkflow?.[workflowId] || {},
  );

  const destinationEntitiesByWorkflow = useSelector(
    (state) => state.entities?.entitiesByWorkflow?.[workflowId] || {},
  );

  const selectedSourceEntitiesByWorkflow = useSelector(
    (state) =>
      state.entities?.selectedSourceEntitiesByWorkflow?.[workflowId] || {},
  );

  const selectedDestinationEntitiesByWorkflow = useSelector(
    (state) =>
      state.entities?.selectedDestinationEntitiesByWorkflow?.[workflowId] || {},
  );

  const confirmedSourceEntitiesByWorkflow = useSelector(
    (state) =>
      state.entities?.confirmedSourceEntitiesByWorkflow?.[workflowId] || {},
  );

  const confirmedDestinationEntitiesByWorkflow = useSelector(
    (state) =>
      state.entities?.confirmedDestinationEntitiesByWorkflow?.[workflowId] ||
      {},
  );

  const sourceErrorByWorkflow = useSelector(
    (state) => state.entities?.sourceErrorByWorkflow?.[workflowId] || {},
  );

  const destinationErrorByWorkflow = useSelector(
    (state) => state.entities?.destinationErrorByWorkflow?.[workflowId] || {},
  );

  useEffect(() => {
    const datasets = [
      {
        scope: 'source',
        errors: sourceErrorByWorkflow,
        keyMap: sourceNodeKeyMap,
      },
      {
        scope: 'destination',
        errors: destinationErrorByWorkflow,
        keyMap: destinationNodeKeyMap,
      },
    ];

    datasets.forEach(({ scope, errors, keyMap }) => {
      if (!errors || typeof errors !== 'object') return;
      Object.entries(errors).forEach(([key, err]) => {
        if (!err || typeof err !== 'object' || !err.needsHostPortUpdate) return;
        const toastKey = `${workflowId || 'default'}-${scope}-${key}-${
          err.message || ''
        }-${err.host || ''}-${err.port || ''}`;
        if (sqlErrorToastKeysRef.current.has(toastKey)) return;
        sqlErrorToastKeysRef.current.add(toastKey);
        const hostText = err.host ? `Host: ${err.host}` : null;
        const portText = err.port ? `Port: ${err.port}` : null;
        const extra = [hostText, portText].filter(Boolean).join(' | ');
        toast.error(
          `Unable to load SQL tables for ${err.connName || 'SQL connection'}. ${
            err.message ||
            'Please update the SQL host and port from the Connections screen.'
          }${extra ? ` (${extra})` : ''}`,
        );

        if (typeof onRequestSqlConnectionFix === 'function') {
          const nodesList = scope === 'source' ? sourceNodes : destNodes;
          const targetNode =
            keyMap?.get(key) ||
            findNodeByConnection(nodesList, err) ||
            nodesList[0];
          if (targetNode) {
            onRequestSqlConnectionFix({
              nodeId: targetNode.id,
              scope,
              key,
              connId: targetNode.config?.connection?.conn_id || null,
              connectionName:
                err.connName ||
                targetNode.config?.connection?.conn_name ||
                targetNode.config?.label ||
                targetNode.data?.label ||
                'SQL Connection',
              host:
                err.host ||
                targetNode.config?.sqlMetadata?.host ||
                targetNode.config?.connection?.sqlMetadata?.host ||
                '',
              port:
                err.port ||
                targetNode.config?.sqlMetadata?.port ||
                targetNode.config?.connection?.sqlMetadata?.port ||
                '',
            });
          }
        }
      });
    });
  }, [
    destinationErrorByWorkflow,
    sourceErrorByWorkflow,
    workflowId,
    sourceNodeKeyMap,
    destinationNodeKeyMap,
    onRequestSqlConnectionFix,
    sourceNodes,
    destNodes,
  ]);

  const allSelectedSourceEntities = sourceNodes.flatMap(
    (sourceNode) =>
      selectedSourceEntitiesByWorkflow[
        `${sourceNode.id}-${sourceNode.config?.connection?.conn_id || ''}-${
          sourceNode.config?.module || ''
        }`
      ] || [],
  );

  const allSelectedDestinationEntities = destNodes.flatMap(
    (destNode) =>
      selectedDestinationEntitiesByWorkflow[
        `${destNode.id}-${destNode.config?.connection?.conn_id || ''}-${
          destNode.config?.module || ''
        }`
      ] || [],
  );

  const lastExpandAllTrigger = useSelector(
    (state) => state.mapping?.expandAllTriggerByWorkflow?.[workflowId] || 0,
  );

  const canGoNext =
    sourceNodes.some((sourceNode) => {
      const sourceKey = buildNodeKey(sourceNode);
      const confirmedSourceSelected =
        confirmedSourceEntitiesByWorkflow[sourceKey] || [];
      const currentSourceSelected =
        selectedSourceEntitiesByWorkflow[sourceKey] || [];
      return !areSelectionsEqual(
        confirmedSourceSelected,
        currentSourceSelected,
      );
    }) ||
    destNodes.some((destNode) => {
      const destKey = buildNodeKey(destNode);
      const confirmedDestSelected =
        confirmedDestinationEntitiesByWorkflow[destKey] || [];
      const currentDestSelected =
        selectedDestinationEntitiesByWorkflow[destKey] || [];
      return !areSelectionsEqual(confirmedDestSelected, currentDestSelected);
    });

  const { darkMode } = useThemeContext();

  const buildPayload = (node) => {
    if (!node || !node.config) return;
    return {
      nodeId: node.id,
      label: node.config.label,
      config: {
        id: node.id,
        label: node.config.label,
        module: node.config.module,
        batches: node.config.batches || 1,
        connection: node.config.connection,
      },
    };
  };

  // Normalize entity selection keys from workflow state to match current node keys
  // This handles cases where saved keys have empty module (trailing "-") but current keys have module
  const normalizeEntityKey = useCallback((savedKey, nodes, targetType) => {
    if (!savedKey || !nodes || !Array.isArray(nodes)) return savedKey;

    // Filter nodes by target type
    const allNodes = nodes.filter(
      (n) =>
        (targetType === 'source' &&
          (n.type === 'sourceNode' || n.type === 'source')) ||
        (targetType === 'destination' &&
          (n.type === 'destinationNode' || n.type === 'destination')),
    );

    // Try to match by connId first (most reliable since it's a UUID)
    // The saved key format is: "nodeId-connId-module" or "nodeId-connId-"
    for (const node of allNodes) {
      const nodeConnId = node?.config?.connection?.conn_id || '';
      const nodeId = node?.id || '';
      if (!nodeConnId || !nodeId) continue;

      // Build the expected key patterns
      const nodeModule = node?.config?.module || '';
      // Normalize SQL module to lowercase
      const normalizedModule =
        nodeModule && nodeModule.toLowerCase() === 'sql' ? 'sql' : nodeModule;

      const keyWithModule = `${nodeId}-${nodeConnId}-${normalizedModule}`;
      const keyWithoutModule = `${nodeId}-${nodeConnId}-`;

      // Check if saved key matches either pattern
      if (savedKey === keyWithModule) {
        return savedKey; // Already correct
      }

      // Check if saved key matches the pattern without module (ends with "-")
      // and starts with nodeId-connId-
      if (
        savedKey === keyWithoutModule ||
        (savedKey.endsWith('-') &&
          savedKey.startsWith(`${nodeId}-${nodeConnId}-`))
      ) {
        // Saved key has empty module, return key with module
        return keyWithModule;
      }
    }

    return savedKey; // Return original if no match found
  }, []);

  // Effect to normalize and load entity selections from workflow state
  useEffect(() => {
    if (!open || !workflowId || !entitySelections || !updatedConfig?.nodes)
      return;

    const { source = {}, destination = {} } = entitySelections;
    const allNodes = updatedConfig.nodes || [];

    // Normalize and set source entity selections
    Object.entries(source).forEach(([savedKey, entities]) => {
      if (!Array.isArray(entities) || entities.length === 0) return;

      const normalizedKey = normalizeEntityKey(savedKey, allNodes, 'source');
      const existing = selectedSourceEntitiesByWorkflow[normalizedKey] || [];

      // Only set if not already set or if empty
      if (!existing || existing.length === 0) {
        console.log(
          `🔄 EntitySelectionDialog: Normalizing source key: "${savedKey}" -> "${normalizedKey}"`,
          entities,
        );
        dispatch(
          setConfirmedSourceEntities({
            workflowId,
            key: normalizedKey,
            entities,
          }),
        );
        dispatch(
          setSelectedSourceEntities({
            workflowId,
            key: normalizedKey,
            entities,
          }),
        );
      }
    });

    // Normalize and set destination entity selections
    Object.entries(destination).forEach(([savedKey, entities]) => {
      if (!Array.isArray(entities) || entities.length === 0) return;

      const normalizedKey = normalizeEntityKey(
        savedKey,
        allNodes,
        'destination',
      );
      const existing =
        selectedDestinationEntitiesByWorkflow[normalizedKey] || [];

      // Only set if not already set or if empty
      if (!existing || existing.length === 0) {
        console.log(
          `🔄 EntitySelectionDialog: Normalizing destination key: "${savedKey}" -> "${normalizedKey}"`,
          entities,
        );
        dispatch(
          setConfirmedDestinationEntities({
            workflowId,
            key: normalizedKey,
            entities,
          }),
        );
        dispatch(
          setSelectedDestinationEntities({
            workflowId,
            key: normalizedKey,
            entities,
          }),
        );
      }
    });
  }, [
    open,
    workflowId,
    entitySelections,
    updatedConfig,
    normalizeEntityKey,
    selectedSourceEntitiesByWorkflow,
    selectedDestinationEntitiesByWorkflow,
    dispatch,
  ]);

  useEffect(() => {
    if (!open || !workflowId) return;
    sourceNodes.forEach((sourceNode) => {
      const sourceKey = buildNodeKey(sourceNode);
      dispatch(
        fetchEntitiesIfNeeded({
          workflowId,
          key: sourceKey,
          targetType: 'source',
          payload: {
            ...buildPayload(sourceNode),
            workflowId,
          },
        }),
      );
    });
    destNodes.forEach((destNode) => {
      const destKey = buildNodeKey(destNode);
      dispatch(
        fetchEntitiesIfNeeded({
          workflowId,
          key: destKey,
          targetType: 'destination',
          payload: {
            ...buildPayload(destNode),
            workflowId,
          },
        }),
      );
    });
  }, [open, workflowId, updatedConfig]);

  const handleNext = async () => {
    sourceNodes.forEach((sourceNode) => {
      const sourceKey = buildNodeKey(sourceNode);
      dispatch(
        setConfirmedSourceEntities({
          workflowId,
          key: sourceKey,
          entities: selectedSourceEntitiesByWorkflow[sourceKey] || [],
        }),
      );
    });

    destNodes.forEach((destNode) => {
      const destKey = buildNodeKey(destNode);
      dispatch(
        setConfirmedDestinationEntities({
          workflowId,
          key: destKey,
          entities: selectedDestinationEntitiesByWorkflow[destKey] || [],
        }),
      );
    });

    const sources = sourceNodes.map((sourceNode) => {
      const sourceKey = buildNodeKey(sourceNode);
      return {
        nodeId: sourceNode.id,
        label: sourceNode.config.label,
        module: sourceNode.config.module,
        connection: sourceNode.config.connection,
        selectedEntities: selectedSourceEntitiesByWorkflow[sourceKey] || [],
      };
    });
    // if (onClose) onClose();

    // 2. Prepare entity config payload (source & destination)
    // const sourceNode = updatedConfig?.nodes?.find(n => n.type === 'sourceNode');
    // const destNode = updatedConfig?.nodes?.find(n => n.type === 'destinationNode');

    let destinations = destNodes.map((destNode) => {
      const destKey = buildNodeKey(destNode);
      return {
        nodeId: destNode.id,
        label: destNode.config.label,
        module: destNode.config.module,
        connection: destNode.config.connection,
        selectedEntities: selectedDestinationEntitiesByWorkflow[destKey] || [],
      };
    });

    if (restrictToActiveDest && activeDestinationId) {
      destinations = destinations.filter(
        (d) => d.nodeId === activeDestinationId,
      );
    }

    const finalEntitySelectionPayload = {
      workflowId,
      source: sources,
      destination: destinations,
      threshold: 80,
    };

    // try {
    //   await dispatch(runFuzzyLogicIfNeeded(finalEntitySelectionPayload));
    // } catch (err) {
    //   console.error('Error running fuzzy logic:', err);
    // }

    dispatch(
      setExpandAllTrigger({
        workflowId,
        trigger: lastExpandAllTrigger + 1,
      }),
    );

    setTimeout(async () => {
      dispatch(
        setAutoFuzzyEnabled({
          workflowId,
          enabled: !shouldSkipFuzzy && effectiveAutoFuzzy,
        }),
      );

      if (!shouldSkipFuzzy && effectiveAutoFuzzy) {
        console.log('should skip fuzzy:', shouldSkipFuzzy);

        try {
          await dispatch(runFuzzyLogicIfNeeded(finalEntitySelectionPayload));
        } catch (err) {
          console.error('Error running fuzzy logic:', err);
        }
      }

      const rawNodes = updatedConfig?.nodes || [];
      const nodesForConfig =
        restrictToActiveDest && activeDestinationId
          ? [
              ...rawNodes.filter(
                (n) => n.type === 'sourceNode' || n.type === 'source',
              ),
              ...rawNodes.filter(
                (n) =>
                  (n.type === 'destinationNode' || n.type === 'destination') &&
                  n.id === activeDestinationId,
              ),
            ]
          : rawNodes;

      const updatedConfigs = nodesForConfig
        ?.map((node) => {
          const nodeKey = buildNodeKey(node);
          const isSource = node.type === 'sourceNode' || node.type === 'source';
          const isDestination =
            node.type === 'destinationNode' || node.type === 'destination';

          if (isSource || isDestination) {
            // Ensure SQL nodes have proper label and module
            let label = node.config?.label;
            let module = node.config?.module;

            // If label or module is missing for SQL nodes, derive from connection or id
            if (!label || !module) {
              const connectorName =
                node.config?.connection?.connector_name ||
                node.config?.connection?.connector ||
                '';
              const isSql =
                connectorName?.toLowerCase() === 'sql' ||
                module?.toLowerCase() === 'sql' ||
                (node.id && node.id.toLowerCase().startsWith('sql-'));

              if (isSql) {
                if (!module) {
                  module = 'sql';
                }
                if (!label) {
                  // Build label from connector name and type
                  const typeLabel = isSource ? 'Source' : 'Destination';
                  label = `SQL (${typeLabel})`;
                }
              }
            }

            return {
              id: node.id,
              label: label || node.config?.label,
              connection: node.config?.connection,
              module: module || node.config?.module,
              selectedEntities: isSource
                ? selectedSourceEntitiesByWorkflow[nodeKey] || []
                : selectedDestinationEntitiesByWorkflow[nodeKey] || [],
              type: isSource ? 'source' : 'destination',
            };
          }
          return null;
        })
        .filter(Boolean);

      if (hasVisitedMapping === false) {
        dispatch(setHasVisitedMapping({ workflowId }));
      }

      const allSelectedSourceEntities = sources.flatMap(
        (s) => s.selectedEntities,
      );
      const allSelectedDestinationEntities = destinations.flatMap(
        (d) => d.selectedEntities,
      );

      const isDecision =
        selectedNode?.type === 'integrationNode' &&
        /^decision-/i.test(selectedNode?.id);

      console.log('isDecision:::', isDecision);

      if (isDecision) {
        const decisionSourcePayload = (updatedConfigs || []).filter(
          (c) => c.type === 'source',
        );
        try {
          // Helper function to check if config is SQL
          // Matches the pattern used in mappingSlice.js and other components
          const isSqlConfig = (config) => {
            const connector =
              config?.connection?.connector_name ||
              config?.connection?.connector ||
              config?.module ||
              config?.label ||
              config?.type ||
              '';
            const isSql = typeof connector === 'string' && connector.toLowerCase().includes('sql');
            if (isSql) {
              console.log('SQL config detected:', {
                id: config.id,
                connector_name: config?.connection?.connector_name,
                module: config?.module,
                label: config?.label,
              });
            }
            return isSql;
          };

          // Helper function to get SQL metadata
          const ensureSqlMetadataForConfig = async (config = {}) => {
            if (!config) return null;
            const direct = config.sqlMetadata || config.connection?.sqlMetadata || null;
            if (direct?.token) return direct;
            
            // Try to get from connection
            if (config.connection?.conn_id) {
              try {
                const connection = await getConnectionById(config.connection.conn_id);
                const params = connection?.custom_params || {};
                const token =
                  params.token || params.quick_test_token || connection?.token || null;
                if (token) return { token };
              } catch (err) {
                console.error('Failed to get SQL metadata:', err);
              }
            }
            return direct || null;
          };

          // Helper function to normalize SQL fields response
          const normalizeSqlFieldsResponse = (response) => {
            if (!response) return [];
            const candidates =
              response.fields?.fields ||
              response.fields ||
              response.columns ||
              response.schema ||
              response.data ||
              response.result ||
              response;
            const asArray = (value) => {
              if (Array.isArray(value)) return value;
              if (value && typeof value === 'object') return Object.values(value);
              return [];
            };
            return asArray(candidates)
              .map((field) => {
                if (!field || typeof field !== 'object') return null;
                const fieldName =
                  field.field_name ||
                  field.column_name ||
                  field.name ||
                  field.columnName ||
                  '';
                if (!fieldName) return null;
                const label =
                  field.field_label ||
                  field.display_name ||
                  field.column_label ||
                  field.column_name ||
                  field.name ||
                  fieldName;
                return {
                  field_name: fieldName,
                  field_label: label,
                };
              })
              .filter(Boolean);
          };

          // Separate SQL and non-SQL configs
          const sqlConfigs = decisionSourcePayload.filter(isSqlConfig);
          const nonSqlConfigs = decisionSourcePayload.filter(
            (cfg) => !isSqlConfig(cfg),
          );

          console.log('Decision node field resolution:', {
            total: decisionSourcePayload.length,
            sqlCount: sqlConfigs.length,
            nonSqlCount: nonSqlConfigs.length,
            sqlConfigs: sqlConfigs.map((c) => ({
              id: c.id,
              connector: c?.connection?.connector_name,
              module: c?.module,
            })),
          });

          let fields = [];

          // Handle non-SQL configs (e.g., Salesforce) - ONLY for non-SQL sources
          // IMPORTANT: Double-check that no SQL configs are included
          const verifiedNonSqlConfigs = nonSqlConfigs.filter((cfg) => !isSqlConfig(cfg));
          if (verifiedNonSqlConfigs.length !== nonSqlConfigs.length) {
            console.error('WARNING: Some SQL configs were incorrectly included in non-SQL API call!');
          }
          
          if (verifiedNonSqlConfigs.length > 0) {
            console.log('Calling resolveFieldsService for non-SQL configs:', verifiedNonSqlConfigs.length);
            const resolved = await resolveFieldsService({
              label: 'FieldResolution',
              configs: verifiedNonSqlConfigs,
            });
            fields = Array.isArray(resolved) ? resolved : [];
          }

          // Handle SQL configs - MUST use fetchSqlTableFields API
          if (sqlConfigs.length > 0) {
            console.log('Using fetchSqlTableFields for SQL configs:', sqlConfigs.length);
            const sqlResolved = await Promise.all(
              sqlConfigs.map(async (config) => {
                const nodeId = config.id || config.nodeId;
                if (!nodeId) {
                  console.warn('SQL config missing id/nodeId:', config);
                  return [];
                }

                const metadata = await ensureSqlMetadataForConfig(config);
                if (!metadata?.token) {
                  console.error(
                    `SQL connector "${
                      config?.connection?.conn_name || config?.label || nodeId
                    }" is missing credentials. Cannot fetch fields.`,
                  );
                  return [];
                }

                const entities = Array.isArray(config.selectedEntities)
                  ? config.selectedEntities
                  : [];
                
                if (entities.length === 0) {
                  console.warn('SQL config has no selected entities:', {
                    nodeId,
                    config: config.id,
                  });
                  return [];
                }

                console.log(`Fetching SQL fields for ${entities.length} entities using fetchSqlTableFields API`);
                const perEntity = await Promise.all(
                  entities.map(async (entity) => {
                    const tableName =
                      entity?.entity_name || entity?.entity || entity?.label || entity;
                    if (!tableName) {
                      console.warn('SQL entity missing table name:', entity);
                      return null;
                    }
                    try {
                      console.log(`Calling fetchSqlTableFields for table: ${tableName}`);
                      const response = await fetchSqlTableFields(
                        metadata.token,
                        tableName,
                      );
                      const fields = normalizeSqlFieldsResponse(response);
                      console.log(`Fetched ${fields.length} fields for table: ${tableName}`);
                      return {
                        node_id: nodeId,
                        type: config.type || 'source',
                        entity: tableName,
                        entity_name: tableName,
                        connection: config.connection,
                        fields: {
                          status: 'success',
                          source: config.connection?.connector_name || 'SQL',
                          module_name: 'SQL',
                          entity_name: tableName,
                          fields,
                        },
                      };
                    } catch (err) {
                      console.error(
                        `Failed to fetch SQL fields for table ${tableName}:`,
                        err,
                      );
                      return null;
                    }
                  }),
                );
                return perEntity.filter(Boolean);
              }),
            );
            fields = [...fields, ...sqlResolved.flat()];
          }

          const byId = new Map(
            (decisionSourcePayload || []).map((s) => [s.id, s]),
          );
          const enriched = (Array.isArray(fields) ? fields : []).map((grp) => {
            // Handle both nodeId (non-SQL) and node_id (SQL) formats
            const nodeId = grp.nodeId || grp.node_id;
            const src = byId.get(nodeId);
            // Extract entity - prioritize grp.entity, then entity_name, then from source config
            const entity =
              grp.entity ||
              grp.entity_name ||
              grp.fields?.entity_name ||
              src?.selectedEntities?.[0]?.entity_name ||
              src?.selectedEntities?.[0]?.label ||
              src?.label ||
              '';
            const entityKey =
              grp.entityKey ||
              (src
                ? `${src.id}-${src.connection?.conn_id || ''}-${
                    src.module || ''
                  }`
                : undefined);
            // Ensure nodeId and entity are set for consistency
            // Preserve all original fields and ensure entity is always set
            return {
              ...grp,
              nodeId: nodeId || grp.nodeId || grp.node_id,
              entity: entity || grp.entity_name || grp.entity,
              entity_name: entity || grp.entity_name || grp.entity,
              entityKey,
            };
          });
          console.log('Decision enriched fields:', enriched);
          onNext?.(enriched);
        } catch (e) {
          console.error('Decision source-only resolve-fields failed:', e);
        }

        return;
      }

      console.log('selectedNode:::', selectedNode);
      if (onClose) onClose();

      try {
        await dispatch(runFuzzyLogicIfNeeded(finalEntitySelectionPayload));
      } catch (err) {
        console.error('Error running fuzzy logic:', err);
      }

      navigate(`/parentworkflow/${workflowId || 'defaultWorkflow'}/mapping`, {
        state: {
          finalConfigPayload: updatedConfigs,
          selectedSourceEntities: allSelectedSourceEntities,
          selectedDestinationEntities: allSelectedDestinationEntities,
          canGoNext,
          decisionMeta,
          secondaryDestinationId,
          shouldSkipFuzzy,
        },
      });
    }, 0);
  };

  const handleSourceSearchToggle = (key) => {
    setSourceSearchVisible((prev) => ({ ...prev, [key]: !prev[key] }));
    setSourceSearchTerm((prev) => ({ ...prev, [key]: '' }));
  };

  const handleDestinationSearchToggle = (key) => {
    setDestinationSearchVisible((prev) => ({ ...prev, [key]: !prev[key] }));
    setDestinationSearchTerm((prev) => ({ ...prev, [key]: '' }));
  };

  const handleConfirmUnselect = () => {
    if (
      pendingUnselectType === 'source' &&
      pendingUnselectEntity &&
      pendingUnselectKey
    ) {
      const selected =
        selectedSourceEntitiesByWorkflow[pendingUnselectKey] || [];

      const updated = selected.filter(
        (e) => e.entity_name !== pendingUnselectEntity.entity_name,
      );

      dispatch(
        setSelectedSourceEntities({
          workflowId,
          entities: updated,
          key: pendingUnselectKey,
        }),
      );
    }

    if (
      pendingUnselectType === 'destination' &&
      pendingUnselectEntity &&
      pendingUnselectKey
    ) {
      const selected =
        selectedDestinationEntitiesByWorkflow[pendingUnselectKey] || [];

      const updated = selected.filter(
        (e) => e.entity_name !== pendingUnselectEntity.entity_name,
      );
      dispatch(
        setSelectedDestinationEntities({
          workflowId,
          entities: updated,
          key: pendingUnselectKey,
        }),
      );
    }
    setConfirmDialogOpen(false);
    setPendingUnselectEntity(null);
    setPendingUnselectType(null);
    setPendingUnselectKey(null);
  };

  const handleCancelUnselect = () => {
    setConfirmDialogOpen(false);
    setPendingUnselectEntity(null);
    setPendingUnselectType(null);
    setPendingUnselectKey(null);
  };

  const getContainedButtonStyle = () => ({
    fontSize: '0.938rem',
    backgroundColor: '#5965A6',
    padding: '0.2rem 1rem',
    color: '#ffffff',
    textTransform: 'none',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
    fontWeight: 500,
    boxShadow: 'none',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#4a5590',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: '#3b4580',
      boxShadow: 'none',
    },
  });

  const getOutlinedGradientButtonStyle = () => ({
    fontWeight: 500,
    fontSize: '0.938rem',
    padding: '0.2rem 1rem',
    textTransform: 'none',
    border: '1px solid #CCCCCC',
    color: darkMode ? '#ffffff' : '#666666',
    borderRadius: '6px',
    background: 'transparent',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      border: '1px solid #999999',
      background: darkMode
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
    },
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        TransitionComponent={Fade}
        transitionDuration={400}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(39, 34, 34, 0.64)',
            backdropFilter: 'blur(2px)',
          },
        }}
        PaperProps={{
          style: {
            padding: '0rem',
            backgroundColor: darkMode ? '#282828' : '#ffffff',
            borderRadius: '8px',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{
              color: darkMode ? '#ffffff' : '#000000',
              fontSize: '1.25rem',
            }}
          >
            Select Entities
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: darkMode ? '#ffffff' : '#666666',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ px: 3, pt: 2, pb: 1, height: '60vh', overflow: 'hidden' }}
        >
          <Box display="flex" gap={4} height="100%">
            <Box
              flex={1}
              border={`1px solid ${darkMode ? '#4a4a4a' : '#e0e0e0'}`}
              borderRadius={1}
              display="flex"
              flexDirection="column"
              overflow="hidden"
              sx={{ backgroundColor: darkMode ? '#282828' : '#ffffff' }}
            >
              {sourceNodes.map((sourceNode) => {
                const sourceKey = buildNodeKey(sourceNode);

                const allSourceEntities =
                  sourceEntitiesByWorkflow[sourceKey] || [];
                const selectedSourceEntities =
                  selectedSourceEntitiesByWorkflow[sourceKey] || [];

                const sourceLoading = sourceLoadingByWorkflow[sourceKey];
                const sourceError = sourceErrorByWorkflow[sourceKey] || null;

                const sourceNodeLabel =
                  sourceNode?.config?.label || 'Source Entity';

                const isSourceSearchVisible =
                  sourceSearchVisible[sourceKey] || false;
                const srcTerm = sourceSearchTerm[sourceKey] || '';

                const isSearching = srcTerm?.trim().length > 0;
                const filteredList = isSearching
                  ? getFilteredList(allSourceEntities, srcTerm)
                  : sortSelectedToTop(
                      allSourceEntities,
                      selectedSourceEntities,
                    );

                const handleSourceCheckboxChange = (entity) => {
                  if (lockSource) return;
                  const isAlreadySelected = selectedSourceEntities.some(
                    (e) => e.entity_name === entity.entity_name,
                  );

                  if (isAlreadySelected) {
                    setPendingUnselectEntity(entity);
                    setPendingUnselectType('source');
                    setPendingUnselectKey(sourceKey);
                    setConfirmDialogOpen(true);
                    return;
                  }

                  const updated = [...selectedSourceEntities, entity].filter(
                    (e, i, arr) =>
                      arr.findIndex((x) => x.entity_name === e.entity_name) ===
                      i,
                  );

                  dispatch(
                    setSelectedSourceEntities({
                      workflowId,
                      entities: updated,
                      key: sourceKey,
                    }),
                  );
                };

                const buildPayload = () => {
                  if (!sourceNode || !sourceNode.config) return;
                  return {
                    nodeId: sourceNode.id,
                    label: sourceNode.config.label,
                    config: {
                      id: sourceNode.id,
                      label: sourceNode.config.label,
                      module: sourceNode.config.module,
                      batches: sourceNode.config.batches || 1,
                      connection: sourceNode.config.connection,
                    },
                  };
                };

                const handleRefreshEntities = () => {
                  const payload = buildPayload();
                  if (!payload) return;
                  dispatch(
                    fetchEntitiesIfNeeded({
                      workflowId,
                      key: sourceKey,
                      targetType: 'source',
                      payload: {
                        ...payload,
                        workflowId,
                        force: true,
                      },
                    }),
                  );
                };

                return (
                  <>
                    <Box
                      sx={{
                        background: '#5965A6',
                        color: '#ffffff',
                        p: 1.2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          component={'img'}
                          src={SourceIcon}
                          alt="Source Icon"
                          width={20}
                          height={20}
                        ></Box>
                        <Typography fontWeight={500}>
                          {sourceNodeLabel}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRefreshEntities()}
                        >
                          <RefreshIcon
                            fontSize="small"
                            sx={{ color: 'white' }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleSourceSearchToggle(sourceKey)}
                        >
                          <SearchIcon
                            fontSize="small"
                            sx={{ color: 'white' }}
                          />
                        </IconButton>
                      </Box>
                    </Box>

                    {isSourceSearchVisible && (
                      <Box
                        // sx={{
                        //   border: `1px solid ${darkMode ? '#4a4a4a' : '#ccc'}`,
                        //   py: 0.3,
                        //   mb: 1,
                        //   borderRadius: '5px',
                        //   backgroundColor: selectedSourceEntities.some(
                        //     (e) => e.entity_name === entity.entity_name
                        //   )
                        //     ? darkMode
                        //       ? '#1e1e1e'
                        //       : '#f2f2f2'
                        //     : darkMode
                        //     ? '#282828'
                        //     : '#ffffff',
                        //   borderColor: selectedSourceEntities.some(
                        //     (e) => e.entity_name === entity.entity_name
                        //   )
                        //     ? '#0056D2'
                        //     : darkMode
                        //     ? '#4a4a4a'
                        //     : '#ccc',
                        //   display: 'flex',
                        //   alignItems: 'center',
                        //   justifyContent: 'flex-start',
                        //   gap: 1,
                        //   px: 2,
                        //   minHeight: 'auto',
                        //   wordBreak: 'break-word',
                        //   whiteSpace: 'normal',
                        //   width: '100%',
                        //   maxWidth: '100%',
                        //   boxSizing: 'border-box',
                        //   overflowX: 'hidden',
                        // }}
                        sx={{
                          px: 2,
                          py: 1,
                          backgroundColor: darkMode ? '#282828' : '#ffffff',
                        }}
                      >
                        <TextField
                          fullWidth
                          placeholder="Search source entity"
                          value={srcTerm}
                          onChange={(e) =>
                            setSourceSearchTerm((prev) => ({
                              ...prev,
                              [sourceKey]: e.target.value,
                            }))
                          }
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: darkMode
                                  ? '#4a4a4a'
                                  : 'rgba(0, 0, 0, 0.23)',
                              },
                              '&:hover fieldset': {
                                borderColor: darkMode
                                  ? '#6a6a6a'
                                  : 'rgba(0, 0, 0, 0.23)',
                              },
                            },
                            '& .MuiInputBase-input': {
                              color: darkMode ? '#ffffff' : '#000000',
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    handleSourceSearchToggle(sourceKey)
                                  }
                                >
                                  <Close
                                    sx={{
                                      fontSize: 'medium',
                                      color: darkMode ? '#ffffff' : '#000000',
                                    }}
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {isSearching && filteredList.length === 0 && (
                          <Typography variant="caption" color="error">
                            No matching entities found.
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box
                      sx={{
                        p: 2,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        backgroundColor: darkMode ? '#282828' : '#ffffff',
                        flex: 1,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: darkMode ? '#1e1e1e' : '#f5f5f5',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: darkMode ? '#4a4a4a' : '#cccccc',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: darkMode ? '#5a5a5a' : '#aaaaaa',
                          },
                        },
                      }}
                    >
                      {sourceError ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                          gap={2}
                        >
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ mb: 1 }}
                          >
                            {sourceError.status === 504 &&
                              (sourceError.message ||
                                'Gateway Timeout. Please try again.')}
                            {sourceError.status === 500 &&
                              'Something went wrong. Please contact support.'}
                            {sourceError.status === 400 &&
                              'Bad Request. Please check your input or try again.'}
                            {sourceError.status === 404 &&
                              'Not Found. The requested resource was not found.'}
                            {sourceError.status &&
                              ![400, 404, 500, 504].includes(
                                sourceError.status,
                              ) &&
                              `Error ${sourceError.status}: ${
                                sourceError.message || 'Unknown error.'
                              }`}
                            {!sourceError.status &&
                              (typeof sourceError === 'string'
                                ? sourceError
                                : sourceError?.message ||
                                  getErrorMessage(
                                    sourceError,
                                    'Failed to fetch source entities.',
                                  ))}
                          </Typography>
                          {sourceError.status === 504 && (
                            <Button
                              onClick={() => handleRefreshEntities()}
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<RefreshIcon />}
                            >
                              Retry
                            </Button>
                          )}
                        </Box>
                      ) : sourceLoading ||
                        (allSourceEntities &&
                          allSourceEntities.length === 0) ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                          gap={2}
                        >
                          <CircularProgress size={28} color="primary" />
                          <Typography variant="body2" color="textSecondary">
                            Loading {sourceNodeLabel} entities...
                          </Typography>
                        </Box>
                      ) : allSourceEntities && filteredList.length > 0 ? (
                        <>
                          {filteredList.map((entity, idx) => (
                            <Box
                              key={`${entity.label}-${idx}`}
                              sx={{
                                py: 0.5,
                                mb: 0.5,
                                backgroundColor: selectedSourceEntities.some(
                                  (e) => e.entity_name === entity.entity_name,
                                )
                                  ? darkMode
                                    ? '#3a3a3a'
                                    : '#E8EAF6'
                                  : idx % 2 === 0
                                    ? darkMode
                                      ? '#2a2a2a'
                                      : '#EEF2FF'
                                    : darkMode
                                      ? '#282828'
                                      : '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                minHeight: 'auto',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                width: '100%',
                                overflowX: 'hidden',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: darkMode
                                    ? '#3a3a3a'
                                    : '#E8EAF6',
                                },
                              }}
                            >
                              <Checkbox
                                disabled={lockSource}
                                size="small"
                                checked={selectedSourceEntities.some(
                                  (e) => e.entity_name === entity.entity_name,
                                )}
                                onChange={() =>
                                  handleSourceCheckboxChange(entity)
                                }
                                sx={{
                                  color: darkMode ? '#b0b0b0' : '#CCCCCC',
                                  '&.Mui-checked': {
                                    color: '#5965A6',
                                  },
                                }}
                              />
                              <Typography
                                sx={{
                                  color: darkMode ? '#ffffff' : '#333333',
                                  wordBreak: 'break-word',
                                  whiteSpace: 'normal',
                                  flex: 1,
                                  fontSize: '0.875rem',
                                  lineHeight: 1.5,
                                }}
                              >
                                {entity.label}
                              </Typography>
                            </Box>
                          ))}
                        </>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            No source entities found.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                );
              })}
            </Box>

            <Box
              flex={1}
              border={`1px solid ${darkMode ? '#4a4a4a' : '#e0e0e0'}`}
              borderRadius={1}
              display="flex"
              flexDirection="column"
              overflow="hidden"
              sx={{ backgroundColor: darkMode ? '#282828' : '#ffffff' }}
            >
              {destNodes.map((destNode) => {
                const destKey = buildNodeKey(destNode);

                const allDestinationEntities =
                  destinationEntitiesByWorkflow[destKey] || [];
                const selectedDestinationEntities =
                  selectedDestinationEntitiesByWorkflow[destKey] || [];

                const destinationLoading =
                  destinationLoadingByWorkflow[destKey];
                const destinationError =
                  destinationErrorByWorkflow[destKey] || null;
                const destinationNodeLabel =
                  destNode?.config?.label || 'Destination Entity';

                const isDestinationSearchVisible =
                  destinationSearchVisible[destKey] || false;
                const destTerm = destinationSearchTerm[destKey] || '';

                const isDesSearching = destTerm?.trim().length > 0;
                const desFilteredList = isDesSearching
                  ? getFilteredList(allDestinationEntities, destTerm)
                  : sortSelectedToTop(
                      allDestinationEntities,
                      selectedDestinationEntities,
                    );

                const handleDestinationCheckboxChange = (entity) => {
                  const isAlreadySelected = selectedDestinationEntities.some(
                    (e) => e.entity_name === entity.entity_name,
                  );
                  if (isAlreadySelected) {
                    setPendingUnselectEntity(entity);
                    setPendingUnselectType('destination');
                    setPendingUnselectKey(destKey);
                    setConfirmDialogOpen(true);
                    return;
                  }
                  const updated = [
                    ...selectedDestinationEntities,
                    entity,
                  ].filter(
                    (e, i, arr) =>
                      arr.findIndex((x) => x.entity_name === e.entity_name) ===
                      i,
                  );
                  dispatch(
                    setSelectedDestinationEntities({
                      workflowId,
                      entities: updated,
                      key: destKey,
                    }),
                  );
                };

                const buildPayload = () => {
                  if (!destNode || !destNode.config) return;
                  return {
                    nodeId: destNode.id,
                    label: destNode.config.label,
                    config: {
                      id: destNode.id,
                      label: destNode.config.label,
                      module: destNode.config.module,
                      batches: destNode.config.batches || 1,
                      connection: destNode.config.connection,
                    },
                  };
                };
                const handleRefreshEntities = () => {
                  const payload = buildPayload();
                  if (!payload) return;
                  dispatch(
                    fetchEntitiesIfNeeded({
                      workflowId,
                      key: destKey,
                      targetType: 'destination',
                      payload: {
                        ...payload,
                        workflowId,
                        force: true,
                      },
                    }),
                  );
                };

                return (
                  <>
                    <Box
                      sx={{
                        background: '#4A4A4A',
                        color: '#ffffff',
                        p: 1.2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          component={'img'}
                          src={DestinationIcon}
                          alt="Destination Icon"
                          width={27}
                          height={27}
                        ></Box>
                        <Typography fontWeight={500}>
                          {destinationNodeLabel}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRefreshEntities()}
                        >
                          <RefreshIcon
                            fontSize="small"
                            sx={{ color: 'white' }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDestinationSearchToggle(destKey)}
                        >
                          <SearchIcon
                            fontSize="small"
                            sx={{ color: 'white' }}
                          />
                        </IconButton>
                      </Box>
                    </Box>

                    {isDestinationSearchVisible && (
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          backgroundColor: darkMode ? '#282828' : '#ffffff',
                        }}
                      >
                        <TextField
                          fullWidth
                          placeholder="Search destination entity"
                          value={destTerm}
                          onChange={(e) =>
                            setDestinationSearchTerm((prev) => ({
                              ...prev,
                              [destKey]: e.target.value,
                            }))
                          }
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: darkMode
                                  ? '#4a4a4a'
                                  : 'rgba(0, 0, 0, 0.23)',
                              },
                              '&:hover fieldset': {
                                borderColor: darkMode
                                  ? '#6a6a6a'
                                  : 'rgba(0, 0, 0, 0.23)',
                              },
                            },
                            '& .MuiInputBase-input': {
                              color: darkMode ? '#ffffff' : '#000000',
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    handleDestinationSearchToggle(destKey)
                                  }
                                >
                                  <Close
                                    sx={{
                                      fontSize: 'medium',
                                      color: darkMode ? '#ffffff' : '#000000',
                                    }}
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {isDesSearching && desFilteredList.length === 0 && (
                          <Typography variant="caption" color="error">
                            No matching entities found.
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box
                      sx={{
                        p: 2,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        backgroundColor: darkMode ? '#282828' : '#ffffff',
                        flex: 1,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: darkMode ? '#1e1e1e' : '#f5f5f5',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: darkMode ? '#4a4a4a' : '#cccccc',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: darkMode ? '#5a5a5a' : '#aaaaaa',
                          },
                        },
                      }}
                    >
                      {destinationError ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                          gap={2}
                        >
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ mb: 1 }}
                          >
                            {destinationError.status === 504 &&
                              (destinationError.message ||
                                'Gateway Timeout. Please try again.')}
                            {destinationError.status === 500 &&
                              'Something went wrong. Please contact support.'}
                            {destinationError.status === 400 &&
                              'Bad Request. Please check your input or try again.'}
                            {destinationError.status === 404 &&
                              'Not Found. The requested resource was not found.'}
                            {destinationError.status &&
                              ![400, 404, 500, 504].includes(
                                destinationError.status,
                              ) &&
                              `Error ${destinationError.status}: ${
                                destinationError.message || 'Unknown error.'
                              }`}
                            {!destinationError.status &&
                              (typeof destinationError === 'string'
                                ? destinationError
                                : destinationError?.message ||
                                  getErrorMessage(
                                    destinationError,
                                    'Failed to fetch destination entities.',
                                  ))}
                          </Typography>
                          {destinationError.status === 504 && (
                            <Button
                              onClick={() => handleRefreshEntities()}
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<RefreshIcon />}
                            >
                              Retry
                            </Button>
                          )}
                        </Box>
                      ) : destinationLoading ||
                        (allDestinationEntities &&
                          allDestinationEntities.length === 0) ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                          gap={2}
                        >
                          <CircularProgress size={28} color="primary" />
                          <Typography variant="body2" color="textSecondary">
                            Loading {destinationNodeLabel} entities...
                          </Typography>
                        </Box>
                      ) : allDestinationEntities &&
                        desFilteredList.length > 0 ? (
                        <>
                          {desFilteredList.map((entity, idx) => (
                            <Box
                              key={`${entity.label}-${idx}`}
                              sx={{
                                py: 0.5,
                                mb: 0.5,
                                backgroundColor:
                                  selectedDestinationEntities.some(
                                    (e) => e.entity_name === entity.entity_name,
                                  )
                                    ? darkMode
                                      ? '#3a3a3a'
                                      : '#E8EAF6'
                                    : idx % 2 === 0
                                      ? darkMode
                                        ? '#2a2a2a'
                                        : '#EEF2FF'
                                      : darkMode
                                        ? '#282828'
                                        : '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                minHeight: 'auto',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                width: '100%',
                                overflowX: 'hidden',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: darkMode
                                    ? '#3a3a3a'
                                    : '#E8EAF6',
                                },
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={selectedDestinationEntities.some(
                                  (e) => e.entity_name === entity.entity_name,
                                )}
                                onChange={() =>
                                  handleDestinationCheckboxChange(entity)
                                }
                                sx={{
                                  color: darkMode ? '#b0b0b0' : '#CCCCCC',
                                  '&.Mui-checked': {
                                    color: '#5965A6',
                                  },
                                }}
                              />
                              <Typography
                                sx={{
                                  color: darkMode ? '#ffffff' : '#333333',
                                  wordBreak: 'break-word',
                                  whiteSpace: 'normal',
                                  flex: 1,
                                  fontSize: '0.875rem',
                                  lineHeight: 1.5,
                                }}
                              >
                                {entity.label}
                              </Typography>
                            </Box>
                          ))}
                        </>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            No destination entities found.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                );
              })}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            gap: 2,
            justifyContent: hideAutoFuzzy ? 'flex-end' : 'space-between',
            alignItems: 'center',
            backgroundColor: darkMode ? '#282828' : '#ffffff',
          }}
        >
          {!hideAutoFuzzy && (
            <FormControlLabel
              control={
                <Tooltip
                  // title={
                  //   !hasVisitedMapping
                  //     ? 'Auto fuzzy will be available after you visit the mapping step once.'
                  //     : ''
                  // }
                  title={
                    shouldSkipFuzzy
                      ? 'Auto fuzzy is disabled on your first visit after login for existing workflows.'
                      : ''
                  }
                  arrow
                  disableHoverListener={shouldSkipFuzzy}
                >
                  <span>
                    <Checkbox
                      checked={effectiveAutoFuzzy}
                      onChange={(e) =>
                        dispatch(
                          setAutoFuzzyEnabled({
                            workflowId,
                            enabled: e.target.checked,
                          }),
                        )
                      }
                      disabled={shouldSkipFuzzy}
                      sx={{
                        color: darkMode ? '#b0b0b0' : '#CCCCCC',
                        '&.Mui-checked': {
                          color: '#5965A6',
                        },
                      }}
                    />
                  </span>
                </Tooltip>
              }
              label={
                <Typography
                  sx={{
                    color: darkMode ? '#ffffff' : '#000000',
                    fontSize: '0.938rem',
                  }}
                >
                  Auto fuzzy
                </Typography>
              }
            />
          )}
          <Box display="flex" gap={2}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={getOutlinedGradientButtonStyle}
            >
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              variant="contained"
              sx={getContainedButtonStyle}
              // disabled={
              //   (allSelectedSourceEntities?.length ?? 0) === 0 ||
              //   (allSelectedDestinationEntities?.length ?? 0) === 0 ||
              //   (requireEntityChangeForNext &&
              //     !canGoNext &&
              //     !effectiveAutoFuzzy)
              // }

              disabled={
                (!requireEntityChangeForNext &&
                  ((allSelectedSourceEntities?.length ?? 0) === 0 ||
                    (allSelectedDestinationEntities?.length ?? 0) === 0)) ||
                (requireEntityChangeForNext &&
                  !canGoNext &&
                  !effectiveAutoFuzzy)
              }
            >
              Next
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Remove Entity"
        content="Are you sure you want to remove this entity?"
        onCancel={handleCancelUnselect}
        onConfirm={handleConfirmUnselect}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      />
    </>
  );
};

EntitySelectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedNode: PropTypes.object,
  updatedConfig: PropTypes.object.isRequired,
  requireEntityChangeForNext: PropTypes.bool,
  onNext: PropTypes.func,
  restrictToActiveDest: PropTypes.bool,
  activeDestinationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  lockSource: PropTypes.bool,
  decisionMeta: PropTypes.object,
  secondaryDestinationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  hideAutoFuzzy: PropTypes.bool,
  onRequestSqlConnectionFix: PropTypes.func,
};

export default EntitySelectionDialog;
